import cx from "classnames";
import PropTypes from "prop-types";
import React from "react";

import { getBootstrapData } from "@swa-ui/bootstrap";
import { SMALL, useDeviceInfo } from "@swa-ui/browser";
import { Button, Card, Heading, Icon, Text } from "@swa-ui/core";
import { getDotComDomain } from "@swa-ui/inflight";
import i18n from "@swa-ui/locale";
import { formatPassengerName } from "@swa-ui/personal-info";
import { TextContent } from "@swa-ui/placements";

import styles from "./MemberWifiStatusDetails.module.scss";

const FULL_AUTH = "fullAuth";
const MIN_NAME_LENGTH_FOR_WRAPPING = 25;

export const MemberWifiStatusDetails = (props) => {
  const { firstName, lastName, preferredName, wifiAuthenticationState } = props;
  const { screenSize } = useDeviceInfo();
  const { INFLIGHT_PROVISOS_GET_CONNECTED_WIFI } = getBootstrapData("urls") || {};
  const fullAuth = wifiAuthenticationState === FULL_AUTH;
  const shouldShowName = preferredName || (firstName && lastName);

  return (
    <Card className={styles.statusDetailsContainer}>
      <div className={styles.content}>
        <div>
          <div
            className={cx({
              [styles.nameContainer]: fullAuth && shouldShowName,
              [styles.wrapped]: shouldWrapName(),
            })}
          >
            {renderActiveWifiStatus()}
            {shouldShowName && (
              <Heading {...getHeadingProps()}>
                {preferredName
                  ? formatPassengerName({ firstName: preferredName, lastName })
                  : formatPassengerName({ firstName, lastName })}
              </Heading>
            )}
          </div>
          <span className={styles.rapidRewards}>
            <Icon {...getPassengerIconProps()} />
            <TextContent value={i18n("memberWifiStatusDetails__RAPID_REWARDS")} />
          </span>
        </div>
        <div className={styles.buttonContainer}>
          {fullAuth ? renderBrowseButton() : renderWifiButton()}
        </div>
      </div>
    </Card>
  );

  function renderActiveWifiStatus() {
    const classes = cx(styles.wifiStatus, {
      [styles.reversed]: !shouldWrapName(),
    });

    return (
      fullAuth && (
        <div className={classes}>
          <Icon {...getWifiIconProps()} />
          <Text size="xlarge">{i18n("memberWifiStatusDetails__ACTIVE")}</Text>
        </div>
      )
    );
  }

  function renderBrowseButton() {
    return <Button {...getBrowseButtonProps()}>{i18n("memberWifiStatusDetails__BROWSE")}</Button>;
  }

  function renderWifiButton() {
    return (
      <Button fullWidth href={INFLIGHT_PROVISOS_GET_CONNECTED_WIFI}>
        {i18n("memberWifiStatusDetails__GET_CONNECTED")}
      </Button>
    );
  }

  function getWifiIconProps() {
    return {
      background: {
        color: "member-wifi-status-icon",
        type: "circle",
      },
      className: styles.wifiIcon,
      color: "member-wifi-status-wifi-icon",
      name: "Wifi",
      shrink: 0.65,
      size: screenSize === SMALL ? "size24" : "size40",
    };
  }

  function getPassengerIconProps() {
    return {
      className: styles.passengerIcon,
      color: "member-wifi-status-passenger-icon",
      name: "Passenger",
      size: "size18",
    };
  }

  function getBrowseButtonProps() {
    return {
      fullWidth: true,
      href: `${getDotComDomain()}/?clk=WFSL_HP_BRWS`,
      newWindow: true,
    };
  }

  function getHeadingProps() {
    return {
      className: styles.passengerName,
      color: "member-wifi-status-passenger-name",
      headingLevel: 2,
      styleLevel: 3,
    };
  }

  function shouldWrapName() {
    const firstNameToCheck = preferredName || firstName;
    const firstNameShouldWrap = firstNameToCheck?.length >= MIN_NAME_LENGTH_FOR_WRAPPING;
    const lastNameShouldWrap = lastName?.length >= MIN_NAME_LENGTH_FOR_WRAPPING;

    return firstNameShouldWrap || lastNameShouldWrap;
  }
};

MemberWifiStatusDetails.propTypes = {
  /* passenger's first name */
  firstName: PropTypes.string,

  /* passenger's last name */
  lastName: PropTypes.string,

  /* passenger's preferred name */
  preferredName: PropTypes.string,

  /* passenger's current Wi-Fi authentication state */
  wifiAuthenticationState: PropTypes.oneOf(["preAuth", "midAuth", "fullAuth"]).isRequired,
};
